import { Stack, usePathname } from 'expo-router';
import * as SplashScreen from 'expo-splash-screen';
import { Component, useEffect, useRef, type ReactNode } from 'react';
import { Platform, StyleSheet, Text, View } from 'react-native';
import { SafeAreaProvider } from 'react-native-safe-area-context';

import { SignInModal } from '~/components/member';
import { useAppFonts } from '~/hooks/useAppFonts';
import { track } from '~/lib/analytics';
import { trackAmplitude } from '~/lib/analytics/amplitude';
import { colors, fontFamily, space, type } from '~/ui/tokens';

SplashScreen.preventAutoHideAsync();

// Class component only because componentDidCatch has no hook equivalent —
// this is the one place in the app allowed to swallow a render crash instead
// of letting it take the whole page down.
class AnalyticsErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
    state = { hasError: false };

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error: unknown) {
        const message = error instanceof Error ? error.message : String(error);
        const screen = typeof window !== 'undefined' ? window.location?.pathname ?? null : null;
        track('app.error', { message, screen, fatal: true });
    }

    render() {
        if (this.state.hasError) {
            return (
                <View style={errorStyles.frame}>
                    <Text style={errorStyles.title}>Something went wrong</Text>
                    <Text style={errorStyles.body}>Try reloading the page.</Text>
                </View>
            );
        }
        return this.props.children;
    }
}

const errorStyles = StyleSheet.create({
    frame: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: space.related, backgroundColor: colors.cream, padding: space.card },
    title: { fontFamily: fontFamily.bold, ...type.sectionTitle, color: colors.ink },
    body: { fontFamily: fontFamily.regular, ...type.bodyRegular, color: colors.inkSoft }
});

function AnalyticsRouteListener() {
    const pathname = usePathname();
    const previousScreen = useRef<string | null>(null);

    useEffect(() => {
        track('screen.viewed', { screen: pathname, previousScreen: previousScreen.current });
        trackAmplitude('Page Viewed', { page: pathname });
        previousScreen.current = pathname;
    }, [pathname]);

    return null;
}

function RootLayout() {
    const [fontsLoaded, fontError] = useAppFonts();
    const pathname = usePathname();

    useEffect(() => {
        if (fontsLoaded || fontError) {
            SplashScreen.hideAsync();
        }
    }, [fontsLoaded, fontError]);

    // The outgoing screen is marked aria-hidden as the new route mounts. If
    // the element the user just activated (a link, a card) still has DOM
    // focus at that instant — the normal case, since activating it is what
    // triggered the navigation — the browser blocks the aria-hidden and warns
    // ("descendant retained focus"), because a focused element can't
    // legitimately be hidden from assistive tech. Blurring on every route
    // change releases focus before that happens, on every screen, for free.
    useEffect(() => {
        if (Platform.OS !== 'web' || typeof document === 'undefined') return;
        (document.activeElement as HTMLElement | null)?.blur?.();
    }, [pathname]);
  
    // Global web styles:
    // 1. Force cover on react-native-web accessibility images so hotel photos don't stretch
    // 2. Remove ugly default browser focus ring / outline on all input text boxes and textareas
    useEffect(() => {
        if (Platform.OS !== 'web' || typeof document === 'undefined') return;
        if (document.getElementById('ss-web-global-styles')) return;
        const style = document.createElement('style');
        style.id = 'ss-web-global-styles';
        style.textContent = `
            img[class*="accessibilityImage"] {
                object-fit: cover !important;
            }
            input, textarea, select, [contenteditable] {
                outline: none !important;
                outline-style: none !important;
                -webkit-tap-highlight-color: transparent !important;
            }
            input:focus, textarea:focus, select:focus, [contenteditable]:focus,
            input:focus-visible, textarea:focus-visible, select:focus-visible {
                outline: none !important;
                outline-style: none !important;
                box-shadow: none !important;
            }
            /* Chrome/Safari paint a saved-value input (e.g. a remembered last
               name) with an opaque blue-tinted background and swap the text
               color, overriding our own field styling — visible as a stray
               highlight band sitting behind the value. The only way to beat it
               is this inset-shadow trick: an opaque shadow the size of the
               field, in our own field color, painted inside the browser's own
               autofill background. Delaying the transition far past any
               realistic interaction keeps it from ever visibly flashing in. */
            input:-webkit-autofill,
            input:-webkit-autofill:hover,
            input:-webkit-autofill:focus,
            input:-webkit-autofill:active {
                -webkit-box-shadow: 0 0 0 1000px ${colors.card} inset !important;
                box-shadow: 0 0 0 1000px ${colors.card} inset !important;
                -webkit-text-fill-color: ${colors.ink} !important;
                caret-color: ${colors.ink};
                transition: background-color 9999s ease-in-out 0s, color 9999s ease-in-out 0s;
            }
            /* Pressables (search field triggers, nav buttons, modal cards,
               thumbnails, etc.) have no custom focus treatment of their own
               the way text inputs do — left alone they fall back to the
               OS's default auto outline, which on this stack renders as an
               off-brand orange ring, instead of removing it outright (unlike
               inputs above), which would drop focus visibility for
               keyboard/screen-reader users. react-native-web marks every
               such Pressable focusable with tabindex="0" whether or not it
               was also given a semantic accessibilityRole, so that selector
               (rather than button or role=button alone, which only some of
               them get) is what actually reaches all of them. */
            button:focus-visible, [role="button"]:focus-visible, [tabindex="0"]:focus-visible {
                outline: 2px solid ${colors.brand} !important;
                outline-offset: 2px;
            }
        `;
        document.head.appendChild(style);
    }, []);

    // Web can render safely with the system fallback while FontFaceObserver
    // loads the bundled WOFF2 files. Do not leave the entire page white if a
    // browser blocks a font request; native keeps the original splash gate.
    if (Platform.OS !== 'web' && !fontsLoaded && !fontError) {
        return null;
    }

    return (
        <SafeAreaProvider>
            <AnalyticsErrorBoundary>
                <AnalyticsRouteListener />
                <Stack
                    screenOptions={{
                        headerShown: false,
                        contentStyle: { backgroundColor: colors.cream }
                    }}
                />
                <SignInModal />
            </AnalyticsErrorBoundary>
        </SafeAreaProvider>
    );
}

export default RootLayout;
